import { Box, Typography, makeStyles } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

// Shared partner / collaboration card used on the Home and About Us pages.
// Dark gradient slab with a white logo tile, bullet points with purple dot
// markers and an optional external link row.
const useStyles = makeStyles((theme) => ({
  card: {
    position: "relative",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background:
      "linear-gradient(160deg, #1a001a 0%, #2d002d 40%, #3a003a 100%)",
    borderRadius: 20,
    padding: "28px 26px 24px",
    overflow: "hidden",
    border: "1px solid rgba(155,59,155,0.16)",
    boxShadow: "0 10px 30px rgba(76,0,76,0.16)",
    transition: "all 0.45s cubic-bezier(0.22, 1, 0.36, 1)",
    "&::before": {
      content: '""',
      position: "absolute",
      width: 240,
      height: 240,
      top: -110,
      right: -70,
      borderRadius: "50%",
      background:
        "radial-gradient(circle, rgba(199,125,199,0.26), transparent 70%)",
      filter: "blur(40px)",
      pointerEvents: "none",
    },
    "&:hover": {
      transform: "translateY(-6px)",
      borderColor: "rgba(199,125,199,0.35)",
      boxShadow: "0 24px 54px rgba(76,0,76,0.32)",
    },
    [theme.breakpoints.down("xs")]: {
      padding: "24px 20px 22px",
    },
  },
  header: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: "14px",
    marginBottom: 16,
  },
  logoTile: {
    width: 64,
    height: 64,
    minWidth: 64,
    borderRadius: 16,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 8,
    overflow: "hidden",
    boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
  },
  logoImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    border: "none",
  },
  logoFallback: {
    display: "none",
    fontSize: "0.62rem",
    fontWeight: 700,
    color: "#4c004c",
    textAlign: "center",
    lineHeight: 1.2,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: "1rem",
    fontWeight: 700,
    color: "#ffffff",
    letterSpacing: "-0.01em",
    marginBottom: 3,
  },
  tagline: {
    fontSize: "0.78rem",
    color: "rgba(255,255,255,0.7)",
    lineHeight: 1.5,
  },
  points: {
    listStyle: "none",
    margin: "0 0 18px",
    padding: 0,
    position: "relative",
    zIndex: 1,
    "& li": {
      position: "relative",
      paddingLeft: 16,
      fontSize: "0.78rem",
      color: "rgba(255,255,255,0.75)",
      lineHeight: 1.6,
      marginBottom: 7,
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: "0.5em",
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #c77dc7, #9b3b9b)",
      },
    },
  },
  linkRow: {
    marginTop: "auto",
    alignSelf: "flex-start",
    position: "relative",
    zIndex: 1,
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#e0b8e0",
    transition: "color 0.3s ease",
    "&, &:hover, &:focus, &:active, &:visited": {
      textDecoration: "none",
    },
    "&:hover": {
      color: "#ffffff",
    },
    "& svg": {
      fontSize: 15,
      transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
    },
    "&:hover svg": {
      transform: "translateX(4px)",
    },
  },
}));

export const CollabCard = ({ logo, name, tagline, points, link }) => {
  const classes = useStyles();

  return (
    <Box className={classes.card}>
      <Box className={classes.header}>
        <Box className={classes.logoTile}>
          <img
            src={logo}
            alt={name}
            className={classes.logoImg}
            onError={(e) => {
              e.target.style.display = "none";
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = "block";
              }
            }}
          />
          <Typography className={classes.logoFallback}>{name}</Typography>
        </Box>
        <Box className={classes.headerText}>
          <Typography className={classes.name}>{name}</Typography>
          <Typography className={classes.tagline}>{tagline}</Typography>
        </Box>
      </Box>

      {points && points.length > 0 && (
        <ul className={classes.points}>
          {points.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>
      )}

      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.linkRow}
        >
          Visit {name} <ArrowForwardIcon />
        </a>
      )}
    </Box>
  );
};
